import { useMemo } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { DD, POST_TYPES, STATUS_CONFIG, DAYS, MONTHS } from "./constants";
import { daysInMonth, firstDayOfMonth, dateStr, getPostDateStr } from "./utils";
import type { Post, Keyword } from "@shared/schema";

export function CalendarView({
  posts,
  keywords: _keywords,
  month,
  year,
  onClickPost,
  onClickDay,
  onPrevMonth,
  onNextMonth,
}: {
  posts: Post[];
  keywords: Keyword[];
  month: number;
  year: number;
  onClickPost: (p: Post) => void;
  onClickDay: (dateStr: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) {
  const days = daysInMonth(year, month);
  const first = firstDayOfMonth(year, month);
  const cells: (number | null)[] = [];
  const today = new Date();
  const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());

  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const { coveredDays, gaps } = useMemo(() => {
    const covered = new Set<number>();
    for (let d = 1; d <= days; d++) {
      const ds = dateStr(year, month, d);
      if (posts.some(p => getPostDateStr(p) === ds)) {
        covered.add(d);
      }
    }

    const gapRanges: Array<{ start: number; length: number }> = [];
    let gapStart = -1;
    let gapLen = 0;
    for (let d = 1; d <= days; d++) {
      if (!covered.has(d)) {
        if (gapStart === -1) gapStart = d;
        gapLen++;
      } else {
        if (gapLen >= 5) {
          gapRanges.push({ start: gapStart, length: gapLen });
        }
        gapStart = -1;
        gapLen = 0;
      }
    }
    if (gapLen >= 5) {
      gapRanges.push({ start: gapStart, length: gapLen });
    }

    return { coveredDays: covered.size, gaps: gapRanges };
  }, [posts, days, year, month]);

  const gapDays = new Set<number>();
  gaps.forEach(g => {
    for (let i = g.start; i < g.start + g.length; i++) gapDays.add(i);
  });

  const coverageColor = coveredDays >= 20 ? DD.green : DD.accent;

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={onPrevMonth} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors" style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary }} data-testid="button-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="text-lg font-bold text-white" data-testid="text-calendar-month">{MONTHS[month]} {year}</h3>
          <button onClick={onNextMonth} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors" style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary }} data-testid="button-next-month">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: DD.surface, border: `1px solid ${DD.border}` }}>
            <span className="text-sm font-bold font-mono" style={{ color: coverageColor }} data-testid="text-coverage-score">
              {coveredDays}/{days} days
            </span>
            <span className="text-[10px] text-white/30">— aim for 20+</span>
          </div>
          {gaps.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
              <AlertTriangle className="w-3 h-3 text-red-400" />
              <span className="text-[11px] text-red-400 font-medium" data-testid="text-gap-warning">
                {gaps.length} gap{gaps.length > 1 ? "s" : ""} of 5+ days
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px mb-px">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[11px] font-semibold text-white/30 uppercase tracking-wider py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="min-h-[100px]" />;
          const ds = dateStr(year, month, d);
          const isToday = ds === todayStr;
          const isGap = gapDays.has(d);
          const dayPosts = posts.filter(p => getPostDateStr(p) === ds);

          return (
            <div
              key={i}
              className="min-h-[100px] rounded-xl p-1.5 cursor-pointer transition-colors hover:bg-white/[0.02]"
              style={{
                border: isToday ? `1px solid ${DD.accentBorder}` : isGap ? `1px solid rgba(239,68,68,0.15)` : `1px solid ${DD.border}`,
                background: isToday ? `${DD.accent}08` : isGap ? "rgba(239,68,68,0.03)" : DD.surface,
              }}
              onClick={() => onClickDay(ds)}
              data-testid={`calendar-day-${d}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs font-bold ${isToday ? "w-5 h-5 rounded-full flex items-center justify-center" : ""}`} style={isToday ? { color: DD.accent, background: `${DD.accent}20` } : { color: isGap ? "rgba(239,68,68,0.5)" : DD.textTertiary }}>
                  {d}
                </div>
                {isGap && <AlertTriangle className="w-2.5 h-2.5 text-red-400/50" />}
              </div>
              <div className="space-y-0.5">
                {dayPosts.slice(0, 3).map(post => {
                  const pt = POST_TYPES.find(t => t.value === post.postType);
                  const ptColor = pt?.color || DD.green;
                  return (
                    <div
                      key={post.id}
                      className="flex items-center gap-1 px-1 py-0.5 rounded text-[10px] cursor-pointer transition-colors hover:bg-white/[0.04]"
                      style={{ borderLeft: `3px solid ${ptColor}` }}
                      onClick={e => { e.stopPropagation(); onClickPost(post); }}
                      data-testid={`calendar-post-${post.id}`}
                    >
                      <span className="text-white/50 truncate flex-1">{post.content?.slice(0, 15) || "Empty"}</span>
                    </div>
                  );
                })}
                {dayPosts.length > 3 && <div className="text-[9px] text-white/20 text-center">+{dayPosts.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-6 mt-3">
        {POST_TYPES.map(pt => (
          <div key={pt.value} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: pt.color }} />
            <span className="text-[10px] text-white/30">{pt.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
          <span className="text-[10px] text-white/30">Gap (5+ days)</span>
        </div>
      </div>
    </div>
  );
}
